import { useMemo } from 'react';
import { Card } from '../../../molecules/Card/Card';
import {
  isEmpty,
  type ChartBaseProps,
  type ChartRow,
} from '../../../molecules/ChartFrame/chartProps';
import { legendItems, type ChartSeries } from '../../../molecules/ChartFrame/chartTheme';
import { ChartLegend } from '../../../molecules/ChartLegend/ChartLegend';
import { useLegendToggle } from '../../../molecules/ChartLegend/useLegendToggle';
import { KPI, type KPIMetric } from '../../../molecules/KPI/KPI';
import { LineChart } from '../../../organisms/LineChart/LineChart';
import {
  PLOT_HEIGHT,
  cardState,
  pct,
  spanClass,
  ticksFor,
  type WidgetBlockProps,
} from '../shared/cardShell';

/** Shares are percentages, so a full bucket reads 100. */
const SHARE_FULL = 100;

export type ShareOfVoiceOverTimeView = {
  /** `Your share now`, `Biggest gain`, `Brands tracked`. */
  kpis: KPIMetric[];
  /** One row per bucket; each brand key holds its share (or `null` for an empty bucket). */
  rows: ChartRow[];
  xKey: string;
  series: ChartSeries[];
  /** Whether any mention is in scope. */
  hasRows: boolean;
  /** The count a share was computed from — "412" for "27.3% · 412 mentions". */
  formatDetail: (row: ChartRow, key: string) => string | undefined;
  /** Comparison-period reading per row and series; nothing when comparison is off. */
  formatCompare?: ChartBaseProps['formatCompare'];
};

export type ShareOfVoiceOverTimeCardProps = WidgetBlockProps & {
  view?: ShareOfVoiceOverTimeView;
  /** Opens the conversations behind a bucket (index into `rows`). */
  onViewDetails?: (index: number) => void;
  /**
   * Each legend entry shows or hides its brand (the executive view, GP
   * 2026-11-03). Only what is drawn changes: the figures above stay of every
   * brand. Default off.
   */
  toggleable?: boolean;
};

/**
 * Figma `931:36991` — every brand's share of the day's mentions, as lines.
 *
 * Lines of shares, not a stack: five ribbons over seventy daily buckets cannot
 * be read, five lines of the same numbers can — each has a fixed baseline. The
 * KPI quotes a value straight off these rows, so "your share now" *is* the last
 * point of our line rather than a second computation that agrees with it by luck.
 */
export function ShareOfVoiceOverTimeCard({
  status,
  view,
  span = 12,
  chrome,
  onRetry,
  onViewDetails,
  toggleable = false,
}: ShareOfVoiceOverTimeCardProps) {
  const hasData = view !== undefined && view.hasRows && !isEmpty(view.rows, view.series);
  const keys = useMemo(() => view?.series.map((s) => s.key) ?? [], [view]);
  const legend = useLegendToggle(keys);
  return (
    <Card
      {...chrome}
      className={spanClass(span)}
      {...cardState(status, hasData, onRetry)}
      title="Share of voice over time"
      helpText="Each selected brand’s daily share of total mentions across selected brands. A brand can gain mentions while losing share if competitors grow faster."
      kpi={view && <KPI metrics={view.kpis} />}
      chart={
        view && (
          <LineChart
            data={view.rows}
            xKey={view.xKey}
            onViewDetails={onViewDetails}
            series={toggleable ? view.series.filter((s) => !legend.isOff(s.key)) : view.series}
            height={PLOT_HEIGHT}
            legend="none"
            /*
             * Pinned to the full scale. A share's range is a fact about the metric
             * rather than something the data happened to do, and letting Recharts
             * fit the axis to a quiet week would redraw the same shape at a
             * different scale every time the filters moved.
             */
            yDomain={[0, SHARE_FULL]}
            /* Pinned domain, pinned ticks — or Recharts picks badly. See rules §4. */
            yTicks={[0, 20, 40, 60, 80, SHARE_FULL]}
            xTicksTarget={ticksFor(span)}
            formatValue={(v) => pct(v)}
            formatAxisValue={(v) => `${Math.round(v)}%`}
            /* The count the share was computed from — "27.3% · 412 mentions" — so
               a reader can tell a quiet day's big share from a busy day's. */
            formatDetail={view.formatDetail}
            formatCompare={view.formatCompare}
          />
        )
      }
      legend={
        view && (
          <ChartLegend
            items={legendItems(view.series).map((item, i) => ({
              ...item,
              off: toggleable && legend.isOff(keys[i]!),
            }))}
            onToggle={toggleable ? (i) => legend.toggle(keys[i]!) : undefined}
          />
        )
      }
    />
  );
}
